import { type FormEvent, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Award,
  BookOpen,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  ExternalLink,
  GraduationCap,
  Mail,
  MapPin,
  Phone,
  RefreshCw,
  Route as RouteIcon,
  Search,
  SlidersHorizontal,
  Sparkles,
  UsersRound,
  X,
} from "lucide-react";
import Dropdown from "../../components/Shared/Dropdown";
import {
  getAdminRoadmapStudents,
  type AdminRoadmapStudent,
  type RoadmapCertification,
} from "../../api/CertRoadmap/GetAdminRoadmapStudents";
import { GetCredentials } from "../../api/CertRoadmap/GetCredentials";

type StudentTypeFilter = "all" | "platform" | "external";
type SortValue = "createdAt:desc" | "createdAt:asc" | "updatedAt:desc";
type RoadmapFilterValues = {
  search: string;
  studentType: StudentTypeFilter;
  credentialId: string;
  recommendation: string;
  sort: SortValue;
};

const initialRoadmapFilters: RoadmapFilterValues = {
  search: "",
  studentType: "all",
  credentialId: "",
  recommendation: "",
  sort: "createdAt:desc",
};

const recommendationOptions = [
  { value: "", label: "All recommendations" },
  { value: "next", label: "Next step" },
  { value: "later", label: "Later" },
  { value: "completed", label: "Completed" },
  { value: "excluded", label: "Excluded" },
];

const sortOptions: Array<{ value: SortValue; label: string }> = [
  { value: "createdAt:desc", label: "Newest submitted" },
  { value: "createdAt:asc", label: "Oldest submitted" },
  { value: "updatedAt:desc", label: "Recently updated" },
];

const studentTypeOptions: Array<{ value: StudentTypeFilter; label: string }> = [
  { value: "all", label: "All students" },
  { value: "platform", label: "Platform students" },
  { value: "external", label: "External students" },
];

const pageSizeOptions = [
  { value: "10", label: "10" },
  { value: "20", label: "20" },
  { value: "50", label: "50" },
];

const dateFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

const dateTimeFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const formatDate = (value: string, includeTime = false) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return (includeTime ? dateTimeFormatter : dateFormatter).format(date);
};

const getInitials = (firstName: string, lastName: string) =>
  `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "ST";

const getRecommendationLabel = (recommendation: string) => {
  const normalized = recommendation.toLowerCase().replace(/[_-]+/g, " ");
  if (normalized === "next" || normalized === "next steps") return "Next step";
  if (normalized === "completed" || normalized === "completed path") {
    return "Completed";
  }
  if (normalized === "excluded" || normalized === "explicitly excluded") {
    return "Excluded";
  }
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const getRecommendationStyle = (recommendation: string) => {
  const value = recommendation.toLowerCase();
  if (value.includes("next")) {
    return "border-orange-200 bg-orange-50 text-orange-700";
  }
  if (value.includes("complete")) {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (value.includes("later")) {
    return "border-sky-200 bg-sky-50 text-sky-700";
  }
  if (value.includes("exclude")) {
    return "border-slate-200 bg-slate-50 text-slate-600";
  }
  return "border-violet-200 bg-violet-50 text-violet-700";
};

const FilterDropdown = ({
  label,
  value,
  onChange,
  options,
  orientation = "start",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  orientation?: "start" | "end";
}) => {
  const selectedOption = options.find((option) => option.value === value) ?? options[0];

  return (
  <div className="block min-w-0">
    <span className="mb-2 block text-xs font-extrabold uppercase text-[#617178]">
      {label}
    </span>
    <Dropdown
      className="min-w-0"
      title={label}
      indicator={false}
      orientation={orientation}
      shape="curved"
      closeOnSelect
      toggleAriaLabel={`Choose ${label.toLowerCase()}`}
      toggleClassNames="!h-12 !w-full !justify-between !rounded-xl !border-[#dfe5e7] !px-4 !text-start !text-sm !font-semibold !text-[#172b34] !shadow-none focus:!border-secondary focus:!ring-4 focus:!ring-secondary/10"
      menuClassNames="!top-[3.25rem] !w-full min-w-[210px] !max-h-72 !overflow-y-auto"
      toggleImage={
        <>
          <span className="min-w-0 truncate">{selectedOption?.label}</span>
          <ChevronDown size={17} className="shrink-0 text-[#718087]" />
        </>
      }
    >
      <div role="menu" className="space-y-1 px-2">
        {options.map((option) => {
          const selected = option.value === value;

          return (
            <button
              key={option.value || "all"}
              type="button"
              role="menuitemradio"
              aria-checked={selected}
              onClick={() => onChange(option.value)}
              className={`flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-start text-sm font-semibold transition ${
                selected
                  ? "bg-secondary/10 text-secondary"
                  : "text-primary hover:bg-[#f5f7f6]"
              }`}
            >
              <span className="min-w-0 truncate">{option.label}</span>
              {selected && <Check size={16} className="shrink-0" />}
            </button>
          );
        })}
      </div>
    </Dropdown>
  </div>
  );
};

const CertificationCard = ({
  certification,
}: {
  certification: RoadmapCertification;
}) => (
  <article className="flex min-w-0 items-center gap-3 rounded-2xl border border-[#e4e9ea] bg-[#fbfcfc] p-3.5">
    <div className="relative grid size-12 shrink-0 place-items-center overflow-hidden rounded-xl border border-black/5 bg-white text-xs font-black text-primary shadow-sm">
      <span>{certification.code.slice(0, 4)}</span>
      {certification.logo && (
        <img
          src={certification.logo}
          alt=""
          loading="lazy"
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
          className="absolute inset-0 size-full bg-white object-contain p-1.5"
        />
      )}
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-black text-primary">
          {certification.code}
        </span>
        <span
          className={`rounded-full border px-2 py-0.5 text-[10px] font-extrabold ${getRecommendationStyle(
            certification.recommendation
          )}`}
        >
          {getRecommendationLabel(certification.recommendation)}
        </span>
      </div>
      <p className="mt-1 truncate text-sm font-semibold text-[#2f424a]" title={certification.name.en}>
        {certification.name.en}
      </p>
    </div>
  </article>
);

const StudentRoadmapCard = ({ item }: { item: AdminRoadmapStudent }) => {
  const fullName = `${item.student.firstName} ${item.student.lastName}`.trim();

  return (
    <article className="overflow-hidden rounded-3xl border border-black/10 bg-white shadow-[0_14px_35px_rgba(12,48,82,0.07)] transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_45px_rgba(12,48,82,0.11)]">
      <div className="grid min-w-0 gap-6 p-5 lg:grid-cols-[minmax(260px,0.85fr)_minmax(0,1.5fr)] lg:p-6">
        <section className="min-w-0 lg:border-r lg:border-[#e6eaeb] lg:pr-6">
          <div className="flex min-w-0 items-start gap-3.5">
            <div className="grid size-12 shrink-0 place-items-center rounded-2xl bg-primary text-sm font-black text-white shadow-[0_8px_22px_rgba(12,48,82,0.18)]">
              {getInitials(item.student.firstName, item.student.lastName)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate text-lg font-black text-[#162d38]" title={fullName}>
                  {fullName || "Unnamed student"}
                </h2>
                <span
                  className={`rounded-full border px-2.5 py-1 text-[10px] font-extrabold uppercase ${
                    item.isPlatformStudent
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-slate-200 bg-slate-50 text-slate-600"
                  }`}
                >
                  {item.isPlatformStudent ? "Platform student" : "External"}
                </span>
              </div>
              <p className="mt-1 text-xs font-semibold text-[#718087]">
                Roadmap #{item.roadmapId.slice(0, 8).toUpperCase()}
              </p>
            </div>
          </div>

          <div className="mt-5 space-y-2.5 text-sm">
            <a
              href={`mailto:${item.student.email}`}
              className="flex min-w-0 items-center gap-2.5 font-semibold text-[#41545c] transition hover:text-secondary"
            >
              <Mail size={16} className="shrink-0 text-[#879399]" />
              <span className="truncate">{item.student.email}</span>
            </a>
            {item.student.phone && (
              <a
                href={`tel:${item.student.phone}`}
                className="flex items-center gap-2.5 font-semibold text-[#41545c] transition hover:text-secondary"
              >
                <Phone size={16} className="shrink-0 text-[#879399]" />
                <span>{item.student.phone}</span>
              </a>
            )}
            <p className="flex items-center gap-2.5 font-semibold text-[#41545c]">
              <MapPin size={16} className="shrink-0 text-[#879399]" />
              <span>{item.student.country || "Country not provided"}</span>
            </p>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#f1f5f6] px-3 py-1.5 text-xs font-extrabold text-[#40545c]">
              <BookOpen size={14} />
              {item.coursesCount} {item.coursesCount === 1 ? "course" : "courses"}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#fff1eb] px-3 py-1.5 text-xs font-extrabold text-[#aa3d17]">
              <Award size={14} />
              {item.certifications.length} recommendations
            </span>
          </div>

          <Link
            to={`/dashboard/students/${item.student.id}`}
            className="mt-5 inline-flex items-center gap-2 text-sm font-extrabold text-primary transition hover:text-secondary"
          >
            View student profile
            <ExternalLink size={15} />
          </Link>
        </section>

        <section className="min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-extrabold uppercase text-[#89969b]">
                Recommended path
              </p>
              <h3 className="mt-1 text-base font-black text-[#19313c]">
                Certifications
              </h3>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#e4e9ea] bg-white px-3 py-1.5 text-xs font-bold text-[#5e6d73]">
              <Clock3 size={14} />
              Updated {formatDate(item.updatedAt)}
            </span>
          </div>

          {item.certifications.length ? (
            <div className="mt-4 grid min-w-0 gap-3 xl:grid-cols-2">
              {item.certifications.map((certification) => (
                <CertificationCard
                  key={`${item.roadmapId}-${certification.credentialId}-${certification.recommendation}`}
                  certification={certification}
                />
              ))}
            </div>
          ) : (
            <div className="mt-4 rounded-2xl border border-dashed border-[#d9e0e2] bg-[#fafcfc] px-4 py-7 text-center text-sm font-semibold text-[#718087]">
              No certifications match the selected recommendation filter.
            </div>
          )}

          {item.courses.length > 0 && (
            <div className="mt-5 border-t border-[#edf0f1] pt-4">
              <p className="mb-2.5 text-xs font-extrabold uppercase text-[#89969b]">
                Enrolled courses
              </p>
              <div className="flex flex-wrap gap-2">
                {item.courses.map((course) => (
                  <span
                    key={course.courseId}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-[#dfe6e8] bg-white px-3 py-2 text-xs font-bold text-[#40545c]"
                  >
                    <GraduationCap size={14} className="text-secondary" />
                    {course.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>

      <footer className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-[#edf0f1] bg-[#fafcfc] px-5 py-3 text-[11px] font-bold text-[#718087] lg:px-6">
        <span className="inline-flex items-center gap-1.5">
          <CalendarDays size={13} /> Submitted {formatDate(item.submittedAt, true)}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <RefreshCw size={13} /> Last change {formatDate(item.updatedAt, true)}
        </span>
      </footer>
    </article>
  );
};

const RoadmapSkeleton = () => (
  <div className="space-y-4" aria-label="Loading roadmap students">
    {[0, 1, 2].map((item) => (
      <div
        key={item}
        className="animate-pulse rounded-3xl border border-black/5 bg-white p-6"
      >
        <div className="grid gap-6 lg:grid-cols-[minmax(260px,0.85fr)_minmax(0,1.5fr)]">
          <div>
            <div className="flex gap-3">
              <div className="size-12 rounded-2xl bg-slate-200" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-2/3 rounded bg-slate-200" />
                <div className="h-3 w-1/3 rounded bg-slate-100" />
              </div>
            </div>
            <div className="mt-6 space-y-3">
              <div className="h-3 w-4/5 rounded bg-slate-100" />
              <div className="h-3 w-3/5 rounded bg-slate-100" />
              <div className="h-3 w-2/5 rounded bg-slate-100" />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="h-20 rounded-2xl bg-slate-100" />
            <div className="h-20 rounded-2xl bg-slate-100" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const Roadmaps = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState("");
  const [studentType, setStudentType] = useState<StudentTypeFilter>("all");
  const [credentialId, setCredentialId] = useState("");
  const [recommendation, setRecommendation] = useState("");
  const [sort, setSort] = useState<SortValue>("createdAt:desc");
  const [appliedFilters, setAppliedFilters] = useState<RoadmapFilterValues>(
    initialRoadmapFilters
  );

  const [sortBy, sortOrder] = appliedFilters.sort.split(":") as [
    string,
    "asc" | "desc",
  ];
  const isPlatformStudent =
    appliedFilters.studentType === "all"
      ? undefined
      : appliedFilters.studentType === "platform";

  const {
    data: response,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: [
      "admin-roadmap-students",
      page,
      limit,
      appliedFilters.sort,
      appliedFilters.studentType,
      appliedFilters.credentialId,
      appliedFilters.recommendation,
      appliedFilters.search,
    ],
    queryFn: () =>
      getAdminRoadmapStudents({
        page,
        limit,
        sortBy,
        sortOrder,
        isPlatformStudent,
        credentialId: appliedFilters.credentialId || undefined,
        recommendation: appliedFilters.recommendation || undefined,
        q: appliedFilters.search || undefined,
      }),
    placeholderData: (previousData) => previousData,
    staleTime: 30 * 1000,
  });

  const { data: credentialResponse } = useQuery({
    queryKey: ["cert-roadmap-credentials"],
    queryFn: GetCredentials,
    staleTime: 5 * 60 * 1000,
  });

  const students = response?.data?.data ?? [];
  const meta = response?.data?.meta;
  const apiError = response?.error;
  const credentials = credentialResponse?.data ?? [];
  const credentialOptions = useMemo(
    () => [
      { value: "", label: "All credentials" },
      ...credentials.map((credential) => ({
        value: credential.id,
        label: `${credential.code} — ${credential.name.en}`,
      })),
    ],
    [credentials]
  );
  const totalRecommendations = useMemo(
    () => students.reduce((total, student) => total + student.certifications.length, 0),
    [students]
  );
  const totalCourses = useMemo(
    () => students.reduce((total, student) => total + student.coursesCount, 0),
    [students]
  );
  const hasActiveFilters = Boolean(
    appliedFilters.search ||
      appliedFilters.studentType !== "all" ||
      appliedFilters.credentialId ||
      appliedFilters.recommendation ||
      appliedFilters.sort !== "createdAt:desc"
  );
  const hasDraftFilters = Boolean(
    search ||
      studentType !== "all" ||
      credentialId ||
      recommendation ||
      sort !== "createdAt:desc"
  );
  const filtersChanged =
    search.trim() !== appliedFilters.search ||
    studentType !== appliedFilters.studentType ||
    credentialId !== appliedFilters.credentialId ||
    recommendation !== appliedFilters.recommendation ||
    sort !== appliedFilters.sort;

  const applyFilters = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAppliedFilters({
      search: search.trim(),
      studentType,
      credentialId,
      recommendation,
      sort,
    });
    setPage(1);
  };

  const clearFilters = () => {
    setSearch("");
    setStudentType("all");
    setCredentialId("");
    setRecommendation("");
    setSort("createdAt:desc");
    setAppliedFilters(initialRoadmapFilters);
    setPage(1);
  };

  const pageNumbers = useMemo(() => {
    const lastPage = meta?.lastPage ?? 1;
    const start = Math.max(1, Math.min(page - 2, lastPage - 4));
    const end = Math.min(lastPage, Math.max(page + 2, 5));
    return Array.from({ length: Math.max(0, end - start + 1) }, (_, index) => start + index);
  }, [meta?.lastPage, page]);

  const showingFrom = meta?.total ? (meta.page - 1) * meta.limit + 1 : 0;
  const showingTo = meta?.total
    ? Math.min((meta.page - 1) * meta.limit + students.length, meta.total)
    : 0;

  return (
    <div className="space-y-5 pb-8">
      <section className="relative overflow-hidden rounded-[28px] bg-primary px-5 py-6 text-white shadow-[0_20px_45px_rgba(12,48,82,0.16)] sm:px-7 sm:py-7">
        <div className="pointer-events-none absolute -right-20 -top-24 size-72 rounded-full border-[42px] border-white/[0.04]" />
        <div className="pointer-events-none absolute -bottom-24 right-1/3 size-52 rounded-full bg-secondary/10 blur-2xl" />
        <div className="relative flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-extrabold text-white/85 backdrop-blur">
              <Sparkles size={14} className="text-[#ff9f7e]" />
              Certification intelligence
            </span>
            <h1 className="mt-4 text-2xl font-black leading-tight text-white sm:text-3xl">
              Student roadmap overview
            </h1>
            <p className="mt-2 max-w-xl text-sm font-medium leading-6 text-white/65 sm:text-base">
              Review each student&apos;s generated certification path, course context,
              and recommended next move in one place.
            </p>
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex h-11 items-center justify-center gap-2 self-start rounded-xl border border-white/15 bg-white/10 px-4 text-sm font-extrabold text-white transition hover:bg-white/15 disabled:opacity-60 lg:self-auto"
          >
            <RefreshCw size={16} className={isFetching ? "animate-spin" : ""} />
            {isFetching ? "Updating" : "Refresh data"}
          </button>
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-label="Roadmap summary">
        {[
          {
            label: "Matching roadmaps",
            value: meta?.total ?? 0,
            helper: hasActiveFilters ? "With current filters" : "All submitted roadmaps",
            icon: RouteIcon,
            tone: "bg-[#e9f0f5] text-primary",
          },
          {
            label: "Students in view",
            value: students.length,
            helper: `Page ${meta?.page ?? page} of ${meta?.lastPage ?? 1}`,
            icon: UsersRound,
            tone: "bg-[#fff0e9] text-[#c5471d]",
          },
          {
            label: "Recommendations",
            value: totalRecommendations,
            helper: "Visible certification matches",
            icon: Award,
            tone: "bg-[#edf7f2] text-emerald-700",
          },
          {
            label: "Course connections",
            value: totalCourses,
            helper: "Across students in view",
            icon: GraduationCap,
            tone: "bg-[#eef5fb] text-sky-700",
          },
        ].map((stat) => (
          <article
            key={stat.label}
            className="flex items-center gap-4 rounded-2xl border border-black/10 bg-white p-4 shadow-[0_10px_25px_rgba(12,48,82,0.05)]"
          >
            <span className={`grid size-11 shrink-0 place-items-center rounded-2xl ${stat.tone}`}>
              <stat.icon size={20} />
            </span>
            <div className="min-w-0">
              <p className="text-2xl font-black leading-none text-[#17303b]">{stat.value}</p>
              <p className="mt-1.5 text-xs font-extrabold text-[#475b63]">{stat.label}</p>
              <p className="mt-0.5 truncate text-[10px] font-semibold text-[#89959a]">
                {stat.helper}
              </p>
            </div>
          </article>
        ))}
      </section>

      <form
        onSubmit={applyFilters}
        className="rounded-3xl border border-black/10 bg-white p-4 shadow-[0_12px_30px_rgba(12,48,82,0.06)] sm:p-5"
      >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="grid size-9 place-items-center rounded-xl bg-[#fff0e9] text-secondary">
              <SlidersHorizontal size={17} />
            </span>
            <div>
              <h2 className="text-sm font-black text-[#19313c]">Filter roadmaps</h2>
              <p className="text-xs font-semibold text-[#879399]">Find the right student or recommendation</p>
            </div>
          </div>
          {(hasActiveFilters || hasDraftFilters) && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-extrabold text-[#a33f1c] transition hover:bg-[#fff1eb]"
            >
              <X size={14} /> Clear filters
            </button>
          )}
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[1.5fr_repeat(4,minmax(150px,1fr))]">
          <label className="block min-w-0">
            <span className="mb-2 block text-xs font-extrabold uppercase text-[#617178]">
              Search
            </span>
            <span className="relative block">
              <Search
                size={17}
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#879399]"
              />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Name, email or phone"
                className="h-12 w-full rounded-xl border border-[#dfe5e7] bg-white pl-10 pr-9 text-sm font-semibold text-[#172b34] outline-none transition placeholder:font-medium placeholder:text-[#9aa5aa] focus:border-secondary focus:ring-4 focus:ring-secondary/10"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                  className="absolute right-2.5 top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded-lg text-[#879399] transition hover:bg-[#f1f4f5] hover:text-primary"
                >
                  <X size={14} />
                </button>
              )}
            </span>
          </label>

          <FilterDropdown
            label="Student type"
            value={studentType}
            onChange={(value) => setStudentType(value as StudentTypeFilter)}
            options={studentTypeOptions}
          />

          <FilterDropdown
            label="Credential"
            value={credentialId}
            onChange={setCredentialId}
            options={credentialOptions}
          />

          <FilterDropdown
            label="Recommendation"
            value={recommendation}
            onChange={setRecommendation}
            options={recommendationOptions}
          />

          <FilterDropdown
            label="Sort by"
            value={sort}
            onChange={(value) => setSort(value as SortValue)}
            options={sortOptions}
            orientation="end"
          />
        </div>

        <div className="mt-5 flex flex-col gap-3 border-t border-[#edf0f1] pt-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs font-semibold text-[#7b898f]">
            {filtersChanged
              ? "Your filter changes are ready to apply."
              : hasActiveFilters
                ? "Results reflect your currently applied filters."
                : "Choose any filters, then apply them together."}
          </p>
          <button
            type="submit"
            disabled={!filtersChanged || isFetching}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary px-5 text-sm font-extrabold text-white shadow-[0_8px_20px_rgba(12,48,82,0.16)] transition hover:bg-secondary disabled:cursor-not-allowed disabled:bg-[#cbd3d6] disabled:shadow-none sm:min-w-[150px]"
          >
            {isFetching ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <Search size={16} />
            )}
            {isFetching ? "Applying" : "Apply filters"}
          </button>
        </div>
      </form>

      <div className="flex flex-wrap items-center justify-between gap-3 px-1">
        <div>
          <h2 className="text-lg font-black text-[#19313c]">Student roadmaps</h2>
          <p className="mt-0.5 text-xs font-semibold text-[#7c898f]">
            {meta?.total
              ? `Showing ${showingFrom}–${showingTo} of ${meta.total}`
              : "Roadmap submissions and recommendations"}
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs font-bold text-[#66757b]">
          Show
          <Dropdown
            className="w-[76px]"
            title="Rows per page"
            indicator={false}
            orientation="end"
            shape="curved"
            closeOnSelect
            toggleAriaLabel="Choose rows per page"
            toggleClassNames="!h-9 !w-full !justify-between !rounded-xl !border-[#dfe5e7] !px-3 !text-xs !font-extrabold !text-primary !shadow-none"
            menuClassNames="!end-0 !top-[2.75rem] !w-28"
            toggleImage={
              <>
                <span>{limit}</span>
                <ChevronDown size={14} className="shrink-0" />
              </>
            }
          >
            <div role="menu" className="space-y-1 px-2">
              {pageSizeOptions.map((option) => {
                const selected = Number(option.value) === limit;

                return (
                  <button
                    key={option.value}
                    type="button"
                    role="menuitemradio"
                    aria-checked={selected}
                    onClick={() => {
                      setLimit(Number(option.value));
                      setPage(1);
                    }}
                    className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-xs font-extrabold transition ${
                      selected
                        ? "bg-secondary/10 text-secondary"
                        : "text-primary hover:bg-[#f5f7f6]"
                    }`}
                  >
                    {option.label}
                    {selected && <Check size={14} />}
                  </button>
                );
              })}
            </div>
          </Dropdown>
          per page
        </div>
      </div>

      {isLoading ? (
        <RoadmapSkeleton />
      ) : apiError ? (
        <section className="rounded-3xl border border-red-200 bg-white px-5 py-14 text-center shadow-sm">
          <span className="mx-auto grid size-14 place-items-center rounded-2xl bg-red-50 text-red-600">
            <RouteIcon size={24} />
          </span>
          <h2 className="mt-4 text-lg font-black text-[#263b44]">Roadmaps could not be loaded</h2>
          <p className="mx-auto mt-2 max-w-md text-sm font-medium text-[#718087]">
            {apiError.message}
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-5 inline-flex h-10 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-extrabold text-white transition hover:bg-secondary"
          >
            <RefreshCw size={15} /> Try again
          </button>
        </section>
      ) : students.length ? (
        <div className={`space-y-4 transition-opacity ${isFetching ? "opacity-65" : "opacity-100"}`}>
          {students.map((student) => (
            <StudentRoadmapCard key={student.roadmapId} item={student} />
          ))}
        </div>
      ) : (
        <section className="rounded-3xl border border-dashed border-[#ccd6d9] bg-white px-5 py-16 text-center">
          <span className="mx-auto grid size-16 place-items-center rounded-2xl bg-[#eef3f5] text-primary">
            <RouteIcon size={28} />
          </span>
          <h2 className="mt-5 text-xl font-black text-[#233b45]">No roadmaps found</h2>
          <p className="mx-auto mt-2 max-w-md text-sm font-medium leading-6 text-[#718087]">
            {hasActiveFilters
              ? "No students match these filters. Try widening your search or clearing the filters."
              : "Student roadmaps will appear here once they have been submitted."}
          </p>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="mt-5 inline-flex h-10 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-extrabold text-white transition hover:bg-secondary"
            >
              <X size={15} /> Clear all filters
            </button>
          )}
        </section>
      )}

      {meta && meta.lastPage > 1 && (
        <nav
          className="flex flex-col items-center justify-between gap-3 rounded-2xl border border-black/10 bg-white p-3 shadow-sm sm:flex-row"
          aria-label="Roadmap pagination"
        >
          <p className="px-2 text-xs font-bold text-[#718087]">
            Page <span className="text-primary">{meta.page}</span> of {meta.lastPage}
          </p>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={!meta.hasPreviousPage}
              className="grid size-9 place-items-center rounded-xl border border-[#dfe5e7] bg-white text-primary transition hover:border-secondary hover:text-secondary disabled:cursor-not-allowed disabled:opacity-35"
              aria-label="Previous page"
            >
              <ChevronLeft size={17} />
            </button>
            <div className="hidden items-center gap-1.5 sm:flex">
              {pageNumbers.map((pageNumber) => (
                <button
                  type="button"
                  key={pageNumber}
                  onClick={() => setPage(pageNumber)}
                  aria-current={pageNumber === meta.page ? "page" : undefined}
                  className={`grid size-9 place-items-center rounded-xl text-xs font-extrabold transition ${
                    pageNumber === meta.page
                      ? "bg-primary text-white shadow-sm"
                      : "border border-[#dfe5e7] bg-white text-[#53656c] hover:border-secondary hover:text-secondary"
                  }`}
                >
                  {pageNumber}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(meta.lastPage, current + 1))}
              disabled={!meta.hasNextPage}
              className="grid size-9 place-items-center rounded-xl border border-[#dfe5e7] bg-white text-primary transition hover:border-secondary hover:text-secondary disabled:cursor-not-allowed disabled:opacity-35"
              aria-label="Next page"
            >
              <ChevronRight size={17} />
            </button>
          </div>
          <span className="inline-flex items-center gap-1.5 px-2 text-xs font-bold text-[#718087]">
            <CheckCircle2 size={14} className="text-emerald-600" />
            {meta.total} total
          </span>
        </nav>
      )}
    </div>
  );
};

export default Roadmaps;
